"use client";

import { useCallback } from "react";
import { useUniversalAuth } from "../lib/auth/OptimalAuthProvider.js";

/**
 * Universal auth hook that works with both unifiedtime and consultanttickets apps
 * Uses the UniversalAuthProvider to get auth data from any app
 */
export function useAuth() {
  // Check if we're in a server-side rendering context
  if (typeof window === 'undefined') {
    console.log('🔍 useAuth: SSR context, returning default values');
    return {
      user: null,
      userType: null,
      isAuthenticated: false,
      isLoading: false,
      isEmployee: false,
      isAdmin: false,
      isClientUser: false,
      signOut: () => Promise.resolve(),
      getAuthToken: () => null,
    };
  }

  try {
    const auth = useUniversalAuth();
    
    // console.log('🔍 Universal useAuth: Using UniversalAuthProvider', {
    //   hasToken: !!auth.session?.access_token,
    //   isAuthenticated: auth.isAuthenticated,
    //   userEmail: auth.appUser?.email
    // });
    
    return {
      user: auth.appUser ? {
        id: auth.appUser.id,
        email: auth.appUser.email,
        first_name: auth.appUser.first_name,
        last_name: auth.appUser.last_name,
        userType: auth.appUser.role,
        name: `${auth.appUser.first_name || ""} ${auth.appUser.last_name || ""}`.trim() || auth.appUser.email,
      } : null,
      userType: auth.appUser?.roles?.name || auth.appUser?.role || null,
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      isEmployee: auth.isEmployee,
      isAdmin: auth.isAdmin,
      isClientUser: auth.isClientUser,
      signOut: auth.signOut,
      getAuthToken: useCallback(() => {
        return auth.session?.access_token || null;
      }, [auth.session?.access_token]),
    };
  } catch (error) {
    // If UniversalAuthProvider is not available, return default values
    console.warn('useAuth: UniversalAuthProvider not available, returning default values');
    return {
      user: null,
      userType: null,
      isAuthenticated: false,
      isLoading: false,
      isEmployee: false,
      isAdmin: false,
      isClientUser: false,
      signOut: () => Promise.resolve(),
      getAuthToken: () => null,
    };
  }
}

/**
 * Hook that returns just the user profile data (or undefined if not authenticated)
 * Useful when you only need the user data without loading/error states
 */
export function useUser(): any | undefined {
  const { user } = useAuth();
  
return user;
}

/**
 * Hook that returns a boolean indicating if the user is authenticated
 * and data has been loaded successfully
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated, isLoading, user } = useAuth();
  
return isAuthenticated && !isLoading && !!user;
}
